import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.ts';
import { changed, FormModal, FormSection } from '../../components/FormModal.tsx';
import {
  ListToolbar,
  matchesStatus,
  SearchBox,
  StatusFilter,
  type StatusFilterValue,
} from '../../components/ListFilters.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import { ConfirmDialog } from '../../components/Modal.tsx';
import { TabPanel, Tabs, useTabInUrl } from '../../components/Tabs.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  issueFor,
  Loading,
  PageHeader,
  Select,
  Table,
  Textarea,
} from '../../components/ui.tsx';
import { type PlNode, PlStructureTab } from './PlStructure.tsx';
import { matchesSearch } from './Settings.tsx';
import { FPA_PL_NATURES, FPA_SPEND_KINDS, type FpaPlNature, type FpaSpendKind } from '@loot/shared';

type Localized = { 'pt-BR': string; en: string };
interface Option {
  id: string;
  name: string;
}
interface Account {
  id: string;
  code: string | null;
  name: string;
}
interface Group {
  id: string;
  code: string;
  name: Localized;
  description: Localized | null;
  sortOrder: number;
  isActive: boolean;
}
interface Item {
  id: string;
  groupId: string;
  code: string;
  name: Localized;
  description: Localized | null;
  expenseTypeId: string;
  classificationId: string;
  accountingAccountId: string | null;
  kind: FpaSpendKind;
  isActive: boolean;
  revision: number;
  expenseTypeName: string | null;
  classificationName: string | null;
  accountCode: string | null;
  accountName: string | null;
}
interface ItemRequest {
  id: string;
  groupId: string | null;
  name: string;
  description: string | null;
  justification: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  itemId: string | null;
  resolutionNote: string | null;
  createdBy: string;
  createdAt: string;
  requesterName: string | null;
}
interface CatalogData {
  groups: Group[];
  items: Item[];
  requests: ItemRequest[];
  options: { expenseTypes: Option[]; classifications: Option[]; accounts: Account[] } | null;
  actions: { manage: boolean; request: boolean };
  me: string;
}

const REQUEST_TONE = {
  pending: 'info',
  approved: 'ok',
  rejected: 'off',
  cancelled: 'neutral',
} as const;
const blankText = (): Localized => ({ 'pt-BR': '', en: '' });

/** O texto no idioma da tela, com o português como reserva. */
function useLocalized() {
  const { i18n } = useTranslation();
  return (v: Localized | null | undefined) =>
    v ? (i18n.language.startsWith('en') && v.en ? v.en : v['pt-BR']) : '';
}

/** Nome (ou descrição) nos dois idiomas, lado a lado. */
function LocalizedFields({
  label,
  value,
  onChange,
  error,
  required,
  multiline,
}: {
  label: string;
  value: Localized;
  onChange: (v: Localized) => void;
  error?: string;
  required?: boolean;
  multiline?: boolean;
}) {
  const { t } = useTranslation();
  const Control = multiline ? Textarea : Input;
  return (
    <>
      {(['pt-BR', 'en'] as const).map((lang) => (
        <Field
          key={lang}
          label={`${label} (${t(`fpaCatalog.lang.${lang}`)})`}
          error={error}
          required={required}
        >
          <Control
            required={required}
            maxLength={multiline ? 1000 : 200}
            value={value[lang]}
            onChange={(ev) => onChange({ ...value, [lang]: ev.target.value })}
          />
        </Field>
      ))}
    </>
  );
}

const CATALOG_TABS = ['items', 'groups', 'requests'] as const;
type GroupForm = { code: string; name: Localized; description: Localized; sortOrder: number };
type ItemForm = {
  groupId: string;
  code: string;
  name: Localized;
  description: Localized;
  expenseTypeId: string;
  classificationId: string;
  accountingAccountId: string;
  kind: FpaSpendKind;
};
type RequestForm = { groupId: string; name: string; description: string; justification: string };
type ResolveForm = { decision: 'approved' | 'rejected'; itemId: string; note: string };

/**
 * Catálogo de Gastos (ADR 0049): grupos amigáveis e itens que sugerem Tipo de Gasto e
 * Classificação. Quem planeja vê os itens ativos e pede um item novo; o FP&A mantém o Catálogo e
 * resolve os pedidos. A carga do Catálogo definitivo depende de autorização própria.
 */
export function FpaCatalogPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const loc = useLocalized();
  const [tab, setTab] = useTabInUrl(CATALOG_TABS, 'items');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilterValue>('active');
  const [group, setGroup] = useState<{
    id: string | null;
    initial: GroupForm;
    form: GroupForm;
  } | null>(null);
  const [item, setItem] = useState<{ id: string | null; initial: ItemForm; form: ItemForm } | null>(
    null,
  );
  const [request, setRequest] = useState<RequestForm | null>(null);
  const [resolving, setResolving] = useState<{ id: string; form: ResolveForm } | null>(null);
  const [toggling, setToggling] = useState<{
    kind: 'groups' | 'items';
    id: string;
    isActive: boolean;
  } | null>(null);
  const data = useQuery({
    queryKey: ['fpa', 'catalog'],
    queryFn: () => api.get<CatalogData>('/fpa/catalog'),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['fpa', 'catalog'] });
  const saveGroup = useMutation({
    mutationFn: ({ id, form }: NonNullable<typeof group>) =>
      id ? api.put(`/fpa/catalog/groups/${id}`, form) : api.post('/fpa/catalog/groups', form),
    onSuccess: async () => {
      setGroup(null);
      await refresh();
    },
  });
  const saveItem = useMutation({
    mutationFn: ({ id, form }: NonNullable<typeof item>) => {
      const body = { ...form, accountingAccountId: form.accountingAccountId || null };
      return id ? api.put(`/fpa/catalog/items/${id}`, body) : api.post('/fpa/catalog/items', body);
    },
    onSuccess: async () => {
      setItem(null);
      await refresh();
    },
  });
  const saveRequest = useMutation({
    mutationFn: (form: RequestForm) =>
      api.post('/fpa/catalog/requests', { ...form, groupId: form.groupId || null }),
    onSuccess: async () => {
      setRequest(null);
      await refresh();
    },
  });
  const resolve = useMutation({
    mutationFn: ({ id, form }: NonNullable<typeof resolving>) =>
      api.post(`/fpa/catalog/requests/${id}/resolve`, {
        decision: form.decision,
        itemId: form.decision === 'approved' ? form.itemId : null,
        note: form.note,
      }),
    onSuccess: async () => {
      setResolving(null);
      await refresh();
    },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api.post(`/fpa/catalog/requests/${id}/cancel`),
    onSuccess: refresh,
  });
  const toggle = useMutation({
    mutationFn: (x: NonNullable<typeof toggling>) =>
      api.post(`/fpa/catalog/${x.kind}/${x.id}/status`, { isActive: !x.isActive }),
    onSuccess: async () => {
      setToggling(null);
      await refresh();
    },
  });

  if (data.isPending) return <Loading />;
  if (data.isError) return <ErrorBox error={data.error} />;
  const { groups, items, requests, options, actions } = data.data;
  const groupName = (id: string | null) => loc(groups.find((g) => g.id === id)?.name) || '—';
  const tabs = CATALOG_TABS.filter((k) => k !== 'groups' || actions.manage);
  const shownItems = items.filter(
    (i) =>
      matchesStatus(i.isActive, status) &&
      matchesSearch(q, i.code, loc(i.name), loc(i.description), groupName(i.groupId)),
  );
  const shownGroups = groups.filter(
    (g) => matchesStatus(g.isActive, status) && matchesSearch(q, g.code, loc(g.name)),
  );
  const openGroup = (g?: Group) => {
    saveGroup.reset();
    const initial: GroupForm = {
      code: g?.code ?? '',
      name: g?.name ?? blankText(),
      description: g?.description ?? blankText(),
      sortOrder: g?.sortOrder ?? 100,
    };
    setGroup({ id: g?.id ?? null, initial, form: initial });
  };
  const openItem = (i?: Item) => {
    saveItem.reset();
    const initial: ItemForm = {
      groupId: i?.groupId ?? groups.find((g) => g.isActive)?.id ?? '',
      code: i?.code ?? '',
      name: i?.name ?? blankText(),
      description: i?.description ?? blankText(),
      expenseTypeId: i?.expenseTypeId ?? '',
      classificationId: i?.classificationId ?? '',
      accountingAccountId: i?.accountingAccountId ?? '',
      kind: i?.kind ?? 'expense',
    };
    setItem({ id: i?.id ?? null, initial, form: initial });
  };
  const gf = group?.form;
  const setG = (patch: Partial<GroupForm>) =>
    setGroup((x) => (x ? { ...x, form: { ...x.form, ...patch } } : x));
  const itf = item?.form;
  const setI = (patch: Partial<ItemForm>) =>
    setItem((x) => (x ? { ...x, form: { ...x.form, ...patch } } : x));

  return (
    <>
      <PageHeader
        title={t('nav.fpaCatalog')}
        description={t('fpaCatalog.description')}
        actions={
          <>
            {actions.request && (
              <Button
                variant={actions.manage ? 'secondary' : undefined}
                onClick={() => {
                  saveRequest.reset();
                  setRequest({ groupId: '', name: '', description: '', justification: '' });
                }}
              >
                {t('fpaCatalog.requests.add')}
              </Button>
            )}
            {actions.manage && (
              <>
                <Button variant="secondary" onClick={() => openGroup()}>
                  {t('fpaCatalog.groups.add')}
                </Button>
                <Button disabled={groups.length === 0} onClick={() => openItem()}>
                  {t('fpaCatalog.items.add')}
                </Button>
              </>
            )}
          </>
        }
      />

      <FormModal
        open={!!group}
        size="md"
        title={t(group?.id ? 'fpaCatalog.groups.edit' : 'fpaCatalog.groups.add')}
        dirty={!!group && changed(group.initial, group.form)}
        saving={saveGroup.isPending}
        error={saveGroup.error}
        onSave={() => group && saveGroup.mutate(group)}
        onClose={() => setGroup(null)}
      >
        {gf && (
          <FormSection columns={2}>
            <Field label={t('fields.code')} error={issueFor(saveGroup.error, 'code')} required>
              <Input
                required
                className="font-mono"
                maxLength={40}
                value={gf.code}
                onChange={(ev) => setG({ code: ev.target.value.toUpperCase() })}
              />
            </Field>
            <Field label={t('fpaCatalog.sortOrder')}>
              <Input
                type="number"
                min={1}
                max={9999}
                value={gf.sortOrder}
                onChange={(ev) => setG({ sortOrder: Number(ev.target.value) || 100 })}
              />
            </Field>
            <LocalizedFields
              label={t('fields.name')}
              required
              value={gf.name}
              error={issueFor(saveGroup.error, 'name')}
              onChange={(name) => setG({ name })}
            />
            <LocalizedFields
              label={t('fpaCatalog.whatGoesHere')}
              multiline
              value={gf.description}
              onChange={(description) => setG({ description })}
            />
          </FormSection>
        )}
      </FormModal>

      <FormModal
        open={!!item}
        title={t(item?.id ? 'fpaCatalog.items.edit' : 'fpaCatalog.items.add')}
        description={t('fpaCatalog.items.hint')}
        dirty={!!item && changed(item.initial, item.form)}
        saving={saveItem.isPending}
        error={saveItem.error}
        onSave={() => item && saveItem.mutate(item)}
        onClose={() => setItem(null)}
      >
        {itf && options && (
          <FormSection columns={2}>
            <Field label={t('fpaCatalog.group')} required>
              <Select
                required
                value={itf.groupId}
                onChange={(ev) => setI({ groupId: ev.target.value })}
              >
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {loc(g.name)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('fields.code')} error={issueFor(saveItem.error, 'code')} required>
              <Input
                required
                className="font-mono"
                maxLength={40}
                value={itf.code}
                onChange={(ev) => setI({ code: ev.target.value.toUpperCase() })}
              />
            </Field>
            <LocalizedFields
              label={t('fields.name')}
              required
              value={itf.name}
              error={issueFor(saveItem.error, 'name')}
              onChange={(name) => setI({ name })}
            />
            <LocalizedFields
              label={t('fpaCatalog.whatGoesHere')}
              multiline
              value={itf.description}
              onChange={(description) => setI({ description })}
            />
            <Field
              label={t('fpaCatalog.kind')}
              hint={t('fpaCatalog.kindHint')}
              required
              className="sm:col-span-2"
            >
              <Select
                required
                value={itf.kind}
                onChange={(ev) => setI({ kind: ev.target.value as FpaSpendKind })}
              >
                {FPA_SPEND_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`fpaExpense.kinds.${k}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('fpaCatalog.expenseType')} required>
              <Select
                required
                value={itf.expenseTypeId}
                onChange={(ev) => setI({ expenseTypeId: ev.target.value })}
              >
                <option value="">{t('common.choose')}</option>
                {options.expenseTypes.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('fpaCatalog.classification')} required>
              <Select
                required
                value={itf.classificationId}
                onChange={(ev) => setI({ classificationId: ev.target.value })}
              >
                <option value="">{t('common.choose')}</option>
                {options.classifications.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('fpaCatalog.account')} hint={t('fpaCatalog.accountHint')}>
              <Select
                value={itf.accountingAccountId}
                onChange={(ev) => setI({ accountingAccountId: ev.target.value })}
              >
                <option value="">{t('fpaCatalog.noAccount')}</option>
                {options.accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {[a.code, a.name].filter(Boolean).join(' · ')}
                  </option>
                ))}
              </Select>
            </Field>
          </FormSection>
        )}
      </FormModal>

      <FormModal
        open={!!request}
        size="md"
        title={t('fpaCatalog.requests.add')}
        description={t('fpaCatalog.requests.hint')}
        dirty={!!request && (!!request.name || !!request.justification)}
        saving={saveRequest.isPending}
        error={saveRequest.error}
        saveLabel={t('fpaCatalog.requests.send')}
        onSave={() => request && saveRequest.mutate(request)}
        onClose={() => setRequest(null)}
      >
        {request && (
          <FormSection columns={1}>
            <Field label={t('fpaCatalog.requests.name')} required>
              <Input
                required
                maxLength={200}
                value={request.name}
                onChange={(ev) => setRequest({ ...request, name: ev.target.value })}
              />
            </Field>
            <Field label={t('fpaCatalog.group')} hint={t('governance.optional')}>
              <Select
                value={request.groupId}
                onChange={(ev) => setRequest({ ...request, groupId: ev.target.value })}
              >
                <option value="">{t('fpaCatalog.requests.anyGroup')}</option>
                {groups
                  .filter((g) => g.isActive)
                  .map((g) => (
                    <option key={g.id} value={g.id}>
                      {loc(g.name)}
                    </option>
                  ))}
              </Select>
            </Field>
            <Field label={t('fpaCatalog.whatGoesHere')} hint={t('governance.optional')}>
              <Textarea
                maxLength={1000}
                value={request.description}
                onChange={(ev) => setRequest({ ...request, description: ev.target.value })}
              />
            </Field>
            <Field
              label={t('fpaCatalog.requests.justification')}
              error={issueFor(saveRequest.error, 'justification')}
              required
            >
              <Textarea
                required
                maxLength={1000}
                value={request.justification}
                onChange={(ev) => setRequest({ ...request, justification: ev.target.value })}
              />
            </Field>
          </FormSection>
        )}
      </FormModal>

      <FormModal
        open={!!resolving}
        size="md"
        title={t('fpaCatalog.requests.resolve')}
        dirty={!!resolving}
        saving={resolve.isPending}
        error={resolve.error}
        onSave={() => resolving && resolve.mutate(resolving)}
        onClose={() => setResolving(null)}
      >
        {resolving && (
          <FormSection columns={1}>
            <Field label={t('fpaCatalog.requests.decision')} required>
              <Select
                value={resolving.form.decision}
                onChange={(ev) =>
                  setResolving({
                    ...resolving,
                    form: {
                      ...resolving.form,
                      decision: ev.target.value as ResolveForm['decision'],
                    },
                  })
                }
              >
                <option value="approved">{t('fpaCatalog.requests.approve')}</option>
                <option value="rejected">{t('fpaCatalog.requests.reject')}</option>
              </Select>
            </Field>
            {resolving.form.decision === 'approved' && (
              <Field
                label={t('fpaCatalog.requests.item')}
                hint={t('fpaCatalog.requests.itemHint')}
                error={issueFor(resolve.error, 'itemId')}
                required
              >
                <Select
                  required
                  value={resolving.form.itemId}
                  onChange={(ev) =>
                    setResolving({
                      ...resolving,
                      form: { ...resolving.form, itemId: ev.target.value },
                    })
                  }
                >
                  <option value="">{t('common.choose')}</option>
                  {items
                    .filter((i) => i.isActive)
                    .map((i) => (
                      <option key={i.id} value={i.id}>
                        {groupName(i.groupId)} › {loc(i.name)}
                      </option>
                    ))}
                </Select>
              </Field>
            )}
            <Field
              label={t('fpaCatalog.requests.note')}
              error={issueFor(resolve.error, 'note')}
              required={resolving.form.decision === 'rejected'}
            >
              <Textarea
                maxLength={1000}
                value={resolving.form.note}
                onChange={(ev) =>
                  setResolving({ ...resolving, form: { ...resolving.form, note: ev.target.value } })
                }
              />
            </Field>
          </FormSection>
        )}
      </FormModal>

      <ConfirmDialog
        open={!!toggling}
        title={t(toggling?.isActive ? 'fpaCatalog.deactivateTitle' : 'fpaCatalog.activateTitle')}
        description={t('fpaCatalog.toggleText')}
        confirmLabel={t(toggling?.isActive ? 'structure.deactivate' : 'structure.activate')}
        tone={toggling?.isActive ? 'danger' : 'primary'}
        busy={toggle.isPending}
        error={toggle.error}
        onConfirm={() => toggling && toggle.mutate(toggling)}
        onCancel={() => setToggling(null)}
      />
      <ErrorBox error={cancel.error} />

      <div className="border-b border-rule">
        <Tabs
          items={tabs.map((k) => ({
            id: k,
            label: t(`fpaCatalog.tabs.${k}`),
            count:
              k === 'requests'
                ? requests.filter((r) => r.status === 'pending').length || undefined
                : undefined,
          }))}
          value={tab}
          onChange={setTab}
          label={t('nav.fpaCatalog')}
          idPrefix="fpa-catalog"
        />
      </div>

      {tab === 'items' && (
        <TabPanel idPrefix="fpa-catalog" id="items">
          <ListToolbar>
            <SearchBox value={q} onChange={setQ} placeholder={t('fpaCatalog.search')} />
            {actions.manage && <StatusFilter value={status} onChange={setStatus} />}
          </ListToolbar>
          <Table
            head={[
              t('fpaCatalog.group'),
              t('fpaCatalog.item'),
              t('fpaCatalog.kind'),
              ...(actions.manage
                ? [t('fpaCatalog.suggestion'), t('fpaCatalog.revision'), t('fields.status'), '']
                : []),
            ]}
          >
            {shownItems.length === 0 && (
              <EmptyRow colSpan={actions.manage ? 7 : 3}>
                {items.length === 0 ? t('fpaCatalog.items.empty') : t('fpaCatalog.noMatch')}
              </EmptyRow>
            )}
            {shownItems.map((i) => (
              <tr key={i.id}>
                <td className="text-xs">{groupName(i.groupId)}</td>
                <td>
                  {loc(i.name)}
                  {i.description && <div className="text-xs text-muted">{loc(i.description)}</div>}
                  {!actions.manage && (
                    <span className="ml-2 inline-flex align-middle">
                      <MasterDeleteButton table="fpa.spend_item" id={i.id} compact />
                    </span>
                  )}
                </td>
                <td className="text-xs">{t(`fpaExpense.kinds.${i.kind}`)}</td>
                {actions.manage && (
                  <>
                    <td className="text-xs">
                      {i.expenseTypeName} · {i.classificationName}
                      {i.accountName && (
                        <div className="text-muted">
                          {[i.accountCode, i.accountName].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td className="tabular-nums">{i.revision}</td>
                    <td>
                      <Badge tone={i.isActive ? 'ok' : 'off'}>
                        {t(i.isActive ? 'fields.active' : 'fields.inactive')}
                      </Badge>
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <span className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={() => openItem(i)}>
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => {
                            toggle.reset();
                            setToggling({ kind: 'items', id: i.id, isActive: i.isActive });
                          }}
                        >
                          {t(i.isActive ? 'structure.deactivate' : 'structure.activate')}
                        </Button>
                        <MasterDeleteButton table="fpa.spend_item" id={i.id} compact />
                      </span>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </Table>
        </TabPanel>
      )}

      {tab === 'groups' && actions.manage && (
        <TabPanel idPrefix="fpa-catalog" id="groups">
          <ListToolbar>
            <SearchBox value={q} onChange={setQ} placeholder={t('fpaCatalog.search')} />
            <StatusFilter value={status} onChange={setStatus} />
          </ListToolbar>
          <Table head={[t('fields.code'), t('fields.name'), t('fields.status'), '']}>
            {shownGroups.length === 0 && (
              <EmptyRow colSpan={4}>
                {groups.length === 0 ? t('fpaCatalog.groups.empty') : t('fpaCatalog.noMatch')}
              </EmptyRow>
            )}
            {shownGroups.map((g) => (
              <tr key={g.id}>
                <td className="font-mono text-xs">{g.code}</td>
                <td>
                  {loc(g.name)}
                  {g.description && <div className="text-xs text-muted">{loc(g.description)}</div>}
                </td>
                <td>
                  <Badge tone={g.isActive ? 'ok' : 'off'}>
                    {t(g.isActive ? 'fields.active' : 'fields.inactive')}
                  </Badge>
                </td>
                <td className="text-right whitespace-nowrap">
                  <span className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={() => openGroup(g)}>
                      {t('common.edit')}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        toggle.reset();
                        setToggling({ kind: 'groups', id: g.id, isActive: g.isActive });
                      }}
                    >
                      {t(g.isActive ? 'structure.deactivate' : 'structure.activate')}
                    </Button>
                    <MasterDeleteButton table="fpa.spend_group" id={g.id} compact />
                  </span>
                </td>
              </tr>
            ))}
          </Table>
        </TabPanel>
      )}

      {tab === 'requests' && (
        <TabPanel idPrefix="fpa-catalog" id="requests">
          <Table
            head={[
              t('fpaCatalog.requests.name'),
              t('fpaCatalog.requests.requester'),
              t('fields.status'),
              t('fpaCatalog.requests.outcome'),
              '',
            ]}
          >
            {requests.length === 0 && (
              <EmptyRow colSpan={5}>{t('fpaCatalog.requests.empty')}</EmptyRow>
            )}
            {requests.map((r) => (
              <tr key={r.id}>
                <td>
                  {r.name}
                  <div className="text-xs text-muted">{r.justification}</div>
                </td>
                <td className="text-xs">{r.requesterName ?? '—'}</td>
                <td>
                  <Badge tone={REQUEST_TONE[r.status]}>
                    {t(`fpaCatalog.requests.status.${r.status}`)}
                  </Badge>
                </td>
                <td className="text-xs">
                  {r.itemId && loc(items.find((i) => i.id === r.itemId)?.name)}
                  {r.resolutionNote && <div className="text-muted">{r.resolutionNote}</div>}
                </td>
                <td className="text-right whitespace-nowrap">
                  <span className="flex justify-end gap-2">
                    {r.status === 'pending' && actions.manage && r.createdBy !== data.data.me && (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          resolve.reset();
                          setResolving({
                            id: r.id,
                            form: { decision: 'approved', itemId: '', note: '' },
                          });
                        }}
                      >
                        {t('fpaCatalog.requests.resolve')}
                      </Button>
                    )}
                    {r.status === 'pending' && r.createdBy === data.data.me && (
                      <Button
                        variant="secondary"
                        disabled={cancel.isPending}
                        onClick={() => cancel.mutate(r.id)}
                      >
                        {t('fpaCatalog.requests.cancel')}
                      </Button>
                    )}
                    <MasterDeleteButton table="fpa.spend_item_request" id={r.id} compact />
                  </span>
                </td>
              </tr>
            ))}
          </Table>
        </TabPanel>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Linhas gerenciais de P&L e De-Para
// ---------------------------------------------------------------------------

interface Mapping {
  id: string;
  code: string;
  name: string;
  status: 'draft' | 'active' | 'retired';
}
interface Section {
  id: string;
  expenseTypeId: string;
  expenseTypeName: string;
  code: string;
  block: 'result' | 'outside';
  isSubtotal: boolean;
  sign: 1 | -1 | null;
  nature: FpaPlNature | null;
  sortOrder: number;
  notes: string | null;
}
interface Line {
  id: string;
  sectionId: string;
  classificationId: string;
  classificationName: string;
  code: string;
  isActive: boolean;
  /** Sinal próprio na DRE (0104): 0 = os dois sentidos; vazio = o da seção. */
  sign: 1 | -1 | 0 | null;
  sortOrder: number;
  notes: string | null;
}
interface AccountRow {
  id: string;
  accountingAccountId: string;
  accountCode: string | null;
  accountName: string;
  scope: 'line' | 'outside' | 'out_of_scope';
  plLineId: string | null;
  lineCode: string | null;
  notes: string | null;
  reviewStatus: 'pending' | 'confirmed' | 'correct';
  reviewNote: string | null;
  reviewerName: string | null;
  createdBy: string;
  updatedBy: string | null;
}
interface PlData {
  mappings: Mapping[];
  mapping: Mapping | null;
  sections: Section[];
  lines: Line[];
  accounts: AccountRow[];
  nodes: PlNode[];
  options: { expenseTypes: Option[]; classifications: Option[]; accounts: Account[] } | null;
  actions: { manage: boolean; review: boolean; me: string };
}
const MAPPING_TONE = { draft: 'info', active: 'ok', retired: 'neutral' } as const;
const REVIEW_TONE = { pending: 'neutral', confirmed: 'ok', correct: 'accent' } as const;
type SectionForm = {
  expenseTypeId: string;
  code: string;
  block: 'result' | 'outside';
  isSubtotal: boolean;
  /** Sinal nos subtotais da DRE (PR-6): soma, subtrai ou a definir. */
  sign: 1 | -1 | null;
  /** Natureza gerencial (PR-8e): decide o botão do lançamento; vazia = pendente. */
  nature: FpaPlNature | null;
  sortOrder: number;
};
type LineForm = {
  sectionId: string;
  classificationId: string;
  code: string;
  /** Sinal próprio na DRE (0104): 0 = os dois sentidos; vazio = o da seção. */
  sign: 1 | -1 | 0 | null;
  sortOrder: number;
};
type AccountForm = {
  accountingAccountId: string;
  scope: AccountRow['scope'];
  plLineId: string;
  notes: string;
};

/**
 * Linhas gerenciais de P&L (ADR 0049): seções da DRE (um Tipo de Gasto cada, código de dois
 * dígitos) e linhas XX.XX, versionadas no mapeamento gerencial; e o De-Para (conta contábil →
 * linha), preparado pelo FP&A e conferido pela Controladoria. Só o rascunho muda.
 */
export function FpaPlLinesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [mappingId, setMappingId] = useState('');
  const [tab, setTab] = useTabInUrl(['lines', 'structure', 'accounts'] as const, 'lines');
  const [section, setSection] = useState<{ id: string | null; form: SectionForm } | null>(null);
  const [line, setLine] = useState<{ id: string | null; form: LineForm } | null>(null);
  const [account, setAccount] = useState<{ id: string | null; form: AccountForm } | null>(null);
  const [review, setReview] = useState<{
    id: string;
    status: 'confirmed' | 'correct';
    note: string;
  } | null>(null);
  const [removing, setRemoving] = useState<{
    kind: 'sections' | 'lines' | 'accounts';
    id: string;
  } | null>(null);
  const data = useQuery({
    queryKey: ['fpa', 'pl', mappingId],
    queryFn: () => api.get<PlData>(`/fpa/pl${mappingId ? `?mappingVersionId=${mappingId}` : ''}`),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['fpa', 'pl'] });
  const done =
    <T,>(close: (v: null) => void) =>
    async (_: T) => {
      close(null);
      await refresh();
    };
  const saveSection = useMutation({
    mutationFn: ({ id, form }: NonNullable<typeof section>) =>
      id
        ? api.put(`/fpa/pl/sections/${id}`, {
            code: form.code,
            block: form.block,
            isSubtotal: form.isSubtotal,
            sign: form.block === 'result' && !form.isSubtotal ? form.sign : null,
            nature: form.isSubtotal ? null : form.nature,
            sortOrder: form.sortOrder,
          })
        : api.post('/fpa/pl/sections', {
            ...form,
            sign: form.block === 'result' && !form.isSubtotal ? form.sign : null,
            nature: form.isSubtotal ? null : form.nature,
            mappingVersionId: data.data?.mapping?.id,
          }),
    onSuccess: done(setSection),
  });
  const saveLine = useMutation({
    mutationFn: ({ id, form }: NonNullable<typeof line>) =>
      id
        ? api.put(`/fpa/pl/lines/${id}`, {
            code: form.code,
            sign: form.sign,
            sortOrder: form.sortOrder,
            isActive: true,
          })
        : api.post('/fpa/pl/lines', { ...form, mappingVersionId: data.data?.mapping?.id }),
    onSuccess: done(setLine),
  });
  const saveAccount = useMutation({
    mutationFn: ({ id, form }: NonNullable<typeof account>) => {
      const body = {
        scope: form.scope,
        plLineId: form.scope === 'line' ? form.plLineId : null,
        notes: form.notes,
      };
      return id
        ? api.put(`/fpa/pl/accounts/${id}`, body)
        : api.post('/fpa/pl/accounts', {
            ...body,
            accountingAccountId: form.accountingAccountId,
            mappingVersionId: data.data?.mapping?.id,
          });
    },
    onSuccess: done(setAccount),
  });
  const saveReview = useMutation({
    mutationFn: (r: NonNullable<typeof review>) =>
      api.post(`/fpa/pl/accounts/${r.id}/review`, { status: r.status, note: r.note }),
    onSuccess: done(setReview),
  });
  const remove = useMutation({
    mutationFn: (r: NonNullable<typeof removing>) => api.delete(`/fpa/pl/${r.kind}/${r.id}`),
    onSuccess: done(setRemoving),
  });

  if (data.isPending) return <Loading />;
  if (data.isError) return <ErrorBox error={data.error} />;
  const d = data.data;
  const draft = d.mapping?.status === 'draft';
  const manage = d.actions.manage && draft;
  const canReview = d.actions.review && draft;
  const seesAccounts = d.actions.manage || d.actions.review;
  const linesOf = (s: Section) => d.lines.filter((l) => l.sectionId === s.id);
  const sf = section?.form;
  const lf = line?.form;
  const af = account?.form;

  return (
    <>
      <PageHeader
        title={t('nav.fpaPlLines')}
        description={t('fpaPl.description')}
        actions={
          manage ? (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  saveSection.reset();
                  setSection({
                    id: null,
                    form: {
                      expenseTypeId: '',
                      code: '',
                      block: 'result',
                      isSubtotal: false,
                      sign: null,
                      nature: null,
                      sortOrder: 100,
                    },
                  });
                }}
              >
                {t('fpaPl.sections.add')}
              </Button>
              <Button
                disabled={!d.sections.some((s) => !s.isSubtotal)}
                onClick={() => {
                  saveLine.reset();
                  setLine({
                    id: null,
                    form: {
                      sectionId: d.sections.find((s) => !s.isSubtotal)?.id ?? '',
                      classificationId: '',
                      code: '',
                      sign: null,
                      sortOrder: 100,
                    },
                  });
                }}
              >
                {t('fpaPl.lines.add')}
              </Button>
            </>
          ) : null
        }
      />
      {d.mappings.length === 0 ? (
        <p className="rounded-lg border border-rule bg-sheet p-4 text-sm text-muted">
          {t('fpaPl.noMapping')}
        </p>
      ) : (
        <>
          <ListToolbar>
            <div className="w-full sm:max-w-2xl">
              <Select
                aria-label={t('fpaPl.mapping')}
                value={d.mapping?.id ?? ''}
                onChange={(ev) => setMappingId(ev.target.value)}
              >
                {d.mappings.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.code} · {m.name} ({t(`fpaPl.mappingStatus.${m.status}`)})
                  </option>
                ))}
              </Select>
            </div>
            {d.mapping && (
              <Badge tone={MAPPING_TONE[d.mapping.status]}>
                {t(`fpaPl.mappingStatus.${d.mapping.status}`)}
              </Badge>
            )}
          </ListToolbar>
          {!draft && <p className="text-sm text-muted">{t('fpaPl.lockedNote')}</p>}
          <ErrorBox error={remove.error} />

          {d.mapping && (
            <div className="border-b border-rule">
              <Tabs
                items={(seesAccounts
                  ? (['lines', 'structure', 'accounts'] as const)
                  : (['lines', 'structure'] as const)
                ).map((k) => ({
                  id: k,
                  label: t(`fpaPl.tabs.${k}`),
                  count:
                    k === 'accounts'
                      ? d.accounts.length
                      : k === 'structure'
                        ? d.nodes.length
                        : d.lines.length,
                }))}
                value={tab}
                onChange={setTab}
                label={t('nav.fpaPlLines')}
                idPrefix="fpa-pl"
              />
            </div>
          )}

          {tab === 'lines' && (
            <TabPanel idPrefix="fpa-pl" id="lines">
              <Table
                head={[t('fpaPl.code'), t('fpaPl.name'), t('fpaPl.block'), ...(manage ? [''] : [])]}
              >
                {d.sections.length === 0 && (
                  <EmptyRow colSpan={manage ? 4 : 3}>{t('fpaPl.empty')}</EmptyRow>
                )}
                {d.sections.flatMap((s) => [
                  <tr key={s.id} className="bg-paper">
                    <td className="font-mono text-xs font-semibold">{s.code}</td>
                    <td className="font-semibold">
                      {s.expenseTypeName}
                      {s.isSubtotal && (
                        <span className="ml-2">
                          <Badge>{t('fpaPl.subtotal')}</Badge>
                        </span>
                      )}
                    </td>
                    <td className="text-xs">
                      {t(`fpaPl.blocks.${s.block}`)}
                      {!s.isSubtotal && (
                        <span className="block text-muted">
                          {s.nature
                            ? t(`fpaExpense.natures.${s.nature}`)
                            : t('fpaPl.naturePending')}
                        </span>
                      )}
                      {!manage && (
                        <span className="ml-2 inline-flex align-middle">
                          <MasterDeleteButton table="fpa.pl_section" id={s.id} compact />
                        </span>
                      )}
                    </td>
                    {manage && (
                      <td className="text-right whitespace-nowrap">
                        <span className="flex justify-end gap-2">
                          <Button
                            variant="secondary"
                            onClick={() => {
                              saveSection.reset();
                              setSection({
                                id: s.id,
                                form: {
                                  expenseTypeId: s.expenseTypeId,
                                  code: s.code,
                                  block: s.block,
                                  isSubtotal: s.isSubtotal,
                                  sign: s.sign,
                                  nature: s.nature,
                                  sortOrder: s.sortOrder,
                                },
                              });
                            }}
                          >
                            {t('common.edit')}
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={() => setRemoving({ kind: 'sections', id: s.id })}
                          >
                            {t('fpaPl.remove')}
                          </Button>
                          <MasterDeleteButton table="fpa.pl_section" id={s.id} compact />
                        </span>
                      </td>
                    )}
                  </tr>,
                  ...linesOf(s).map((l) => (
                    <tr key={l.id}>
                      <td className="pl-6 font-mono text-xs">{l.code}</td>
                      <td className="text-sm">{l.classificationName}</td>
                      <td>
                        {!manage && <MasterDeleteButton table="fpa.pl_line" id={l.id} compact />}
                      </td>
                      {manage && (
                        <td className="text-right whitespace-nowrap">
                          <span className="flex justify-end gap-2">
                            <Button
                              variant="secondary"
                              onClick={() => {
                                saveLine.reset();
                                setLine({
                                  id: l.id,
                                  form: {
                                    sectionId: l.sectionId,
                                    classificationId: l.classificationId,
                                    code: l.code,
                                    sign: l.sign,
                                    sortOrder: l.sortOrder,
                                  },
                                });
                              }}
                            >
                              {t('common.edit')}
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={() => setRemoving({ kind: 'lines', id: l.id })}
                            >
                              {t('fpaPl.remove')}
                            </Button>
                            <MasterDeleteButton table="fpa.pl_line" id={l.id} compact />
                          </span>
                        </td>
                      )}
                    </tr>
                  )),
                ])}
              </Table>
            </TabPanel>
          )}

          {tab === 'structure' && d.mapping && (
            <TabPanel idPrefix="fpa-pl" id="structure">
              <PlStructureTab
                mappingId={d.mapping.id}
                nodes={d.nodes}
                sections={d.sections}
                lines={d.lines}
                manage={manage}
                onChanged={refresh}
              />
            </TabPanel>
          )}

          {tab === 'accounts' && seesAccounts && (
            <TabPanel idPrefix="fpa-pl" id="accounts">
              <ListToolbar
                action={
                  manage && (
                    <Button
                      onClick={() => {
                        saveAccount.reset();
                        setAccount({
                          id: null,
                          form: { accountingAccountId: '', scope: 'line', plLineId: '', notes: '' },
                        });
                      }}
                    >
                      {t('fpaPl.accounts.add')}
                    </Button>
                  )
                }
              >
                <p className="text-sm text-muted">{t('fpaPl.accounts.hint')}</p>
              </ListToolbar>
              <Table
                head={[
                  t('fpaPl.accounts.account'),
                  t('fpaPl.accounts.destination'),
                  t('fpaPl.accounts.review'),
                  '',
                ]}
              >
                {d.accounts.length === 0 && (
                  <EmptyRow colSpan={4}>{t('fpaPl.accounts.empty')}</EmptyRow>
                )}
                {d.accounts.map((a) => {
                  const mine = a.createdBy === d.actions.me || a.updatedBy === d.actions.me;
                  return (
                    <tr key={a.id}>
                      <td className="text-sm">
                        <span className="font-mono text-xs">{a.accountCode}</span> {a.accountName}
                      </td>
                      <td className="text-sm">
                        {a.scope === 'line' ? (
                          <span className="font-mono">{a.lineCode}</span>
                        ) : (
                          t(`fpaPl.accounts.scopes.${a.scope}`)
                        )}
                        {a.notes && <div className="text-xs text-muted">{a.notes}</div>}
                      </td>
                      <td>
                        <Badge tone={REVIEW_TONE[a.reviewStatus]}>
                          {t(`fpaPl.accounts.reviewStatus.${a.reviewStatus}`)}
                        </Badge>
                        {a.reviewerName && (
                          <div className="text-xs text-muted">{a.reviewerName}</div>
                        )}
                        {a.reviewNote && <div className="text-xs text-muted">{a.reviewNote}</div>}
                      </td>
                      <td className="text-right whitespace-nowrap">
                        <span className="flex justify-end gap-2">
                          {canReview && !mine && (
                            <>
                              <Button
                                variant="secondary"
                                onClick={() => {
                                  saveReview.reset();
                                  setReview({ id: a.id, status: 'confirmed', note: '' });
                                }}
                              >
                                {t('fpaPl.accounts.confirm')}
                              </Button>
                              <Button
                                variant="secondary"
                                onClick={() => {
                                  saveReview.reset();
                                  setReview({ id: a.id, status: 'correct', note: '' });
                                }}
                              >
                                {t('fpaPl.accounts.correct')}
                              </Button>
                            </>
                          )}
                          {manage && (
                            <>
                              <Button
                                variant="secondary"
                                onClick={() => {
                                  saveAccount.reset();
                                  setAccount({
                                    id: a.id,
                                    form: {
                                      accountingAccountId: a.accountingAccountId,
                                      scope: a.scope,
                                      plLineId: a.plLineId ?? '',
                                      notes: a.notes ?? '',
                                    },
                                  });
                                }}
                              >
                                {t('common.edit')}
                              </Button>
                              <Button
                                variant="secondary"
                                onClick={() => setRemoving({ kind: 'accounts', id: a.id })}
                              >
                                {t('fpaPl.remove')}
                              </Button>
                            </>
                          )}
                          <MasterDeleteButton table="fpa.account_map" id={a.id} compact />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </Table>
            </TabPanel>
          )}
        </>
      )}

      <FormModal
        open={!!section}
        size="md"
        title={t(section?.id ? 'fpaPl.sections.edit' : 'fpaPl.sections.add')}
        description={t('fpaPl.sections.hint')}
        dirty={!!section}
        saving={saveSection.isPending}
        error={saveSection.error}
        onSave={() => section && saveSection.mutate(section)}
        onClose={() => setSection(null)}
      >
        {sf && d.options && section && (
          <FormSection columns={2}>
            <Field label={t('fpaCatalog.expenseType')} required>
              <Select
                required
                disabled={!!section.id}
                value={sf.expenseTypeId}
                onChange={(ev) =>
                  setSection({ ...section, form: { ...sf, expenseTypeId: ev.target.value } })
                }
              >
                <option value="">{t('common.choose')}</option>
                {d.options.expenseTypes.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label={t('fpaPl.code')}
              hint={t('fpaPl.sections.codeHint')}
              error={issueFor(saveSection.error, 'code')}
              required
            >
              <Input
                required
                className="font-mono"
                maxLength={2}
                value={sf.code}
                onChange={(ev) =>
                  setSection({ ...section, form: { ...sf, code: ev.target.value } })
                }
              />
            </Field>
            <Field label={t('fpaPl.block')} required>
              <Select
                value={sf.block}
                onChange={(ev) =>
                  setSection({
                    ...section,
                    form: { ...sf, block: ev.target.value as SectionForm['block'] },
                  })
                }
              >
                <option value="result">{t('fpaPl.blocks.result')}</option>
                <option value="outside">{t('fpaPl.blocks.outside')}</option>
              </Select>
            </Field>
            <Field label={t('fpaPl.subtotal')} hint={t('fpaPl.sections.subtotalHint')}>
              <Select
                value={sf.isSubtotal ? 'yes' : 'no'}
                onChange={(ev) =>
                  setSection({ ...section, form: { ...sf, isSubtotal: ev.target.value === 'yes' } })
                }
              >
                <option value="no">{t('common.no')}</option>
                <option value="yes">{t('common.yes')}</option>
              </Select>
            </Field>
            {sf.block === 'result' && !sf.isSubtotal && (
              <Field label={t('fpaPl.sign')} hint={t('fpaPl.signHint')}>
                <Select
                  value={sf.sign === null ? '' : String(sf.sign)}
                  onChange={(ev) =>
                    setSection({
                      ...section,
                      form: {
                        ...sf,
                        sign: ev.target.value === '' ? null : (Number(ev.target.value) as 1 | -1),
                      },
                    })
                  }
                >
                  <option value="">{t('fpaPl.signPending')}</option>
                  <option value="1">{t('fpaPl.signPlus')}</option>
                  <option value="-1">{t('fpaPl.signMinus')}</option>
                </Select>
              </Field>
            )}
            {!sf.isSubtotal && (
              <Field label={t('fpaPl.nature')} hint={t('fpaPl.natureHint')}>
                <Select
                  value={sf.nature ?? ''}
                  onChange={(ev) =>
                    setSection({
                      ...section,
                      form: { ...sf, nature: (ev.target.value || null) as FpaPlNature | null },
                    })
                  }
                >
                  <option value="">{t('fpaPl.naturePending')}</option>
                  {FPA_PL_NATURES.map((n) => (
                    <option key={n} value={n}>
                      {t(`fpaExpense.natures.${n}`)}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </FormSection>
        )}
      </FormModal>

      <FormModal
        open={!!line}
        size="md"
        title={t(line?.id ? 'fpaPl.lines.edit' : 'fpaPl.lines.add')}
        description={t('fpaPl.lines.hint')}
        dirty={!!line}
        saving={saveLine.isPending}
        error={saveLine.error}
        onSave={() => line && saveLine.mutate(line)}
        onClose={() => setLine(null)}
      >
        {lf && d.options && line && (
          <FormSection columns={1}>
            <Field label={t('fpaPl.section')} required>
              <Select
                required
                disabled={!!line.id}
                value={lf.sectionId}
                onChange={(ev) => setLine({ ...line, form: { ...lf, sectionId: ev.target.value } })}
              >
                {d.sections
                  .filter((s) => !s.isSubtotal)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.code} · {s.expenseTypeName}
                    </option>
                  ))}
              </Select>
            </Field>
            <Field label={t('fpaCatalog.classification')} required>
              <Select
                required
                disabled={!!line.id}
                value={lf.classificationId}
                onChange={(ev) =>
                  setLine({ ...line, form: { ...lf, classificationId: ev.target.value } })
                }
              >
                <option value="">{t('common.choose')}</option>
                {d.options.classifications.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label={t('fpaPl.code')}
              hint={t('fpaPl.lines.codeHint')}
              error={issueFor(saveLine.error, 'code')}
              required
            >
              <Input
                required
                className="font-mono"
                maxLength={5}
                value={lf.code}
                onChange={(ev) => setLine({ ...line, form: { ...lf, code: ev.target.value } })}
              />
            </Field>
            {d.sections.find((s) => s.id === lf.sectionId)?.block === 'result' && (
              <Field label={t('fpaPl.lines.sign')} hint={t('fpaPl.lines.signHint')}>
                <Select
                  value={lf.sign === null ? '' : String(lf.sign)}
                  onChange={(ev) =>
                    setLine({
                      ...line,
                      form: {
                        ...lf,
                        sign:
                          ev.target.value === '' ? null : (Number(ev.target.value) as 1 | -1 | 0),
                      },
                    })
                  }
                >
                  <option value="">{t('fpaPl.lines.signSection')}</option>
                  <option value="1">{t('fpaPl.signPlus')}</option>
                  <option value="-1">{t('fpaPl.signMinus')}</option>
                  <option value="0">{t('fpaPl.lines.signBoth')}</option>
                </Select>
              </Field>
            )}
          </FormSection>
        )}
      </FormModal>

      <FormModal
        open={!!account}
        size="md"
        title={t(account?.id ? 'fpaPl.accounts.edit' : 'fpaPl.accounts.add')}
        description={t('fpaPl.accounts.formHint')}
        dirty={!!account}
        saving={saveAccount.isPending}
        error={saveAccount.error}
        onSave={() => account && saveAccount.mutate(account)}
        onClose={() => setAccount(null)}
      >
        {af && d.options && account && (
          <FormSection columns={1}>
            <Field label={t('fpaPl.accounts.account')} required>
              <Select
                required
                disabled={!!account.id}
                value={af.accountingAccountId}
                onChange={(ev) =>
                  setAccount({ ...account, form: { ...af, accountingAccountId: ev.target.value } })
                }
              >
                <option value="">{t('common.choose')}</option>
                {d.options.accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {[a.code, a.name].filter(Boolean).join(' · ')}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('fpaPl.accounts.destination')} required>
              <Select
                value={af.scope}
                onChange={(ev) =>
                  setAccount({
                    ...account,
                    form: { ...af, scope: ev.target.value as AccountForm['scope'] },
                  })
                }
              >
                {(['line', 'outside', 'out_of_scope'] as const).map((s) => (
                  <option key={s} value={s}>
                    {t(`fpaPl.accounts.scopes.${s}`)}
                  </option>
                ))}
              </Select>
            </Field>
            {af.scope === 'line' && (
              <Field
                label={t('fpaPl.accounts.line')}
                error={issueFor(saveAccount.error, 'plLineId')}
                required
              >
                <Select
                  required
                  value={af.plLineId}
                  onChange={(ev) =>
                    setAccount({ ...account, form: { ...af, plLineId: ev.target.value } })
                  }
                >
                  <option value="">{t('common.choose')}</option>
                  {d.lines.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.code} · {l.classificationName}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <Field label={t('fpaPl.accounts.notes')} hint={t('governance.optional')}>
              <Textarea
                maxLength={1000}
                value={af.notes}
                onChange={(ev) =>
                  setAccount({ ...account, form: { ...af, notes: ev.target.value } })
                }
              />
            </Field>
          </FormSection>
        )}
      </FormModal>

      <FormModal
        open={!!review}
        size="md"
        title={t(
          review?.status === 'correct' ? 'fpaPl.accounts.correct' : 'fpaPl.accounts.confirm',
        )}
        description={t('fpaPl.accounts.reviewHint')}
        dirty={!!review}
        saving={saveReview.isPending}
        error={saveReview.error}
        onSave={() => review && saveReview.mutate(review)}
        onClose={() => setReview(null)}
      >
        {review && (
          <FormSection columns={1}>
            <Field
              label={t('fpaPl.accounts.reviewNote')}
              error={issueFor(saveReview.error, 'note')}
              required={review.status === 'correct'}
            >
              <Textarea
                maxLength={1000}
                value={review.note}
                onChange={(ev) => setReview({ ...review, note: ev.target.value })}
              />
            </Field>
          </FormSection>
        )}
      </FormModal>

      <ConfirmDialog
        open={!!removing}
        title={t('fpaPl.removeTitle')}
        description={t('fpaPl.removeText')}
        confirmLabel={t('fpaPl.remove')}
        tone="danger"
        busy={remove.isPending}
        error={remove.error}
        onConfirm={() => removing && remove.mutate(removing)}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}
